import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getAdminContent, getContactSubmissions } from '@/utils/api'

type DashboardStats = {
  submissions: number
  services: number
  media: number
}

type RecentSubmission = {
  id: number
  full_name: string
  email: string
  submitted_at: string
  consultation_type?: string
}

function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats>({ submissions: 0, services: 0, media: 0 })
  const [recentSubmissions, setRecentSubmissions] = useState<RecentSubmission[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true)
      try {
        const [content, submissionsResult] = await Promise.all([
          getAdminContent().catch(() => ({ pages: [], services: [], media: [] } as const)),
          getContactSubmissions(1, 5).catch(() => ({ items: [], total: 0 } as const)),
        ])

        setStats({
          submissions: submissionsResult.total || 0,
          services: content.services?.length || 0,
          media: content.media?.length || 0,
        })

        setRecentSubmissions((submissionsResult.items || []) as RecentSubmission[])
      } catch (error) {
        console.error('Error loading dashboard:', error)
      } finally {
        setLoading(false)
      }
    }

    loadDashboard()
  }, [])

  return (
    <div className="min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text-primary">Resumen</h1>
        <p className="text-text-secondary mt-1">Bienvenido al panel de administración de El Horcon Criollo</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {[
          {
            label: 'Envíos de contacto',
            value: loading ? '...' : stats.submissions,
            icon: 'ph-inbox',
            href: '/admin/contact-submissions',
            color: 'bg-blue-50 text-blue-600',
          },
          {
            label: 'Servicios configurados',
            value: loading ? '...' : stats.services,
            icon: 'ph-stethoscope',
            href: '/admin/content',
            color: 'bg-green-50 text-green-600',
          },
          {
            label: 'Medios',
            value: loading ? '...' : stats.media,
            icon: 'ph-image',
            href: '/admin/media',
            color: 'bg-purple-50 text-purple-600',
          },
        ].map((stat) => (
          <Link
            key={stat.label}
            to={stat.href}
            className="glass-card rounded-xl p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary mb-1">{stat.label}</p>
                <p className="text-4xl font-bold text-text-primary">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <i className={`ph ${stat.icon} text-2xl`}></i>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="glass-card rounded-xl p-6 mb-8">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Acciones rápidas</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { href: '/admin/content', icon: 'ph-article', label: 'Gestionar Contenido' },
            { href: '/admin/media', icon: 'ph-image', label: 'Gestión de Medios' },
            { href: '/admin/contact-submissions', icon: 'ph-inbox', label: 'Envíos de Contacto' },
          ].map((action) => (
            <Link
              key={action.href}
              to={action.href}
              className="p-4 bg-primary/5 rounded-lg hover:bg-primary/10 transition text-center"
            >
              <i className={`ph ${action.icon} text-2xl text-primary mb-2`}></i>
              <p className="text-sm font-medium text-text-primary">{action.label}</p>
            </Link>
          ))}
        </div>
      </div>

      <div className="glass-card rounded-xl p-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Últimos envíos de contacto</h2>
        {loading ? (
          <div className="text-center py-6 text-text-secondary">
            <i className="ph ph-spinner animate-spin text-2xl text-primary mb-2"></i>
            <p>Cargando...</p>
          </div>
        ) : recentSubmissions.length === 0 ? (
          <p className="text-text-secondary">Todavía no hay envíos registrados.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Nombre</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Email</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Tipo</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {recentSubmissions.map((submission, index) => (
                  <tr key={submission.id} className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    <td className="px-4 py-3 text-sm text-text-primary">{submission.full_name}</td>
                    <td className="px-4 py-3 text-sm text-text-primary">{submission.email}</td>
                    <td className="px-4 py-3 text-sm text-text-primary">{submission.consultation_type || 'Contacto'}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary">{new Date(submission.submitted_at).toLocaleString('es-AR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminDashboard